import { useDisclosure } from "@chakra-ui/react"
import {
  AugmentationCoordinatorProvider,
  AugmentationLayoutFrame,
  AugmentationPrimarySurface,
  useAugmentationLayoutSpacing,
  useAugmentationMeasure,
  useAugmentationPanelOffset,
  useAugmentationToolbarOffset
} from "@pathwright/ux/src/components/augmentation/AugmentationCoordinator"
import { AugmentationComponents } from "@pathwright/ux/src/components/augmentation/AugmentationLayout"
import AugmentationPositionedToolbar from "@pathwright/ux/src/components/augmentation/AugmentationPositionedToolbar"
import { useAugmentationDisclosedToolbar } from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import CertificateState from "../context/CertificateState"
import CertificateAugmentation, {
  useCertificateAugmentationToolbar
} from "./CertificateAugmentation"

function CertificateAugmentationInner({
  Primary,
  toolbar,
  disclosure
}: {
  Primary: AugmentationComponents["Primary"]
  toolbar: ReturnType<typeof useCertificateAugmentationToolbar>
  disclosure: ReturnType<typeof useDisclosure>
}) {
  const disclosedToolbar = useAugmentationDisclosedToolbar(toolbar, disclosure)
  const panelRef = useAugmentationMeasure("panel")
  const toolbarRef = useAugmentationMeasure("toolbar")
  const panelOffset = useAugmentationPanelOffset()
  const toolbarOffset = useAugmentationToolbarOffset()
  const layoutSpacing = useAugmentationLayoutSpacing()

  return (
    <AugmentationLayoutFrame>
      {Primary ? (
        <AugmentationPrimarySurface>
          <Primary {...disclosedToolbar} />
        </AugmentationPrimarySurface>
      ) : null}
      {!disclosure.open ? (
        <AugmentationPositionedToolbar
          ref={toolbarRef}
          gutter={layoutSpacing}
          offset={toolbarOffset}
          toolbar={disclosedToolbar}
        />
      ) : null}
      <CertificateAugmentation
        ref={panelRef}
        toolbar={disclosedToolbar}
        disclosure={disclosure}
        gutter={layoutSpacing}
        offset={panelOffset}
      />
    </AugmentationLayoutFrame>
  )
}

const CertificateAugmentationLayout = ({
  Primary,
  children
}: {
  Primary: AugmentationComponents["Primary"]
  children?: React.ReactNode
}) => {
  const toolbar = useCertificateAugmentationToolbar()
  const disclosure = useDisclosure()

  return (
    <CertificateState>
      {children}
      <AugmentationCoordinatorProvider
        disclosure={disclosure}
        spacing={[10, 10, 10]}
        panelPosition="near"
        align="right"
        hasPrimary={!!Primary}
        hasToolbar={!disclosure.open}
      >
        <CertificateAugmentationInner
          Primary={Primary}
          toolbar={toolbar}
          disclosure={disclosure}
        />
      </AugmentationCoordinatorProvider>
    </CertificateState>
  )
}

CertificateAugmentationLayout.displayName = "CertificateAugmentationLayout"

export default CertificateAugmentationLayout
